import React, { useEffect, useState } from "react";
import ScrollToTop from "react-scroll-to-top";
import { useTheme } from '@mui/material/styles';
import config from "../../config/config";
import {
  Button,
  Container,
  Grid,
  Typography,
  Tooltip,
  Modal,
  Box,
  Divider,
  TextField,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import ConfirmModal from '../../components/layout/ConfirmModal';
import { customBookingForm, getBookingFormData } from "../../redux/actions/book";

import IMG_BOOKING_OVERVIEW from "../../assets/img/dashboard-tutorial-03.jpg";

const CustomizeFormPage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => ({
    user: state.auth.user
  }));
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    dispatch(getBookingFormData(user?.bookFormId));
  }, []);

  const emailNotification = useSelector(state => state.book.emailNotification);

  const [valueDirect, setValueDirect] = useState("");
  const [openLinkCopyTooltip, setOpenLinkCopyTooltip] = useState(false);

  const [welcomeTitle, setWelcomeTitle] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState(``);
  const [thankyouTitle, setThankyouTitle] = useState('');
  const [thankyouMessage, setThankyouMessage] = useState(``);
  const [whatsnextMessage, setWhatsnextMessage] = useState('');

  useEffect(() => {
    if(emailNotification.welcomeTitle) {
      setWelcomeTitle(emailNotification.welcomeTitle);
      setWelcomeMessage(emailNotification.welcomeMessage);
      setThankyouTitle(emailNotification.thankyouTitle);
      setThankyouMessage(emailNotification.thankyouMessage);
      setWhatsnextMessage(emailNotification.whatsnextMessage);
    }
  }, [emailNotification]);
								
  useEffect(() => {
    if (user?.firstName) {
      setValueDirect(`${config.CLIENT_BASE_URL}/${user?.username}/book`);
    }
  }, [user]);								

  const handleTooltipClose = () => {
    setOpenLinkCopyTooltip(false);
  };

  const handleCopyLink = () => {
    copyToClipboard(valueDirect);
    setOpenLinkCopyTooltip(true);
  }

  async function copyToClipboard(textToCopy) {
    // Navigator clipboard api needs a secure context (https)
    if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
    } else {
      // Use the 'out of viewport hidden text area' trick
      const textArea = document.createElement("textarea");
      textArea.value = textToCopy;
          
      // Move textarea out of the viewport so it's not visible
      textArea.style.position = "absolute";
      textArea.style.left = "-999999px";
          
      document.body.prepend(textArea);
      textArea.select();

      try {
        document.execCommand('copy');
      } catch (error) {
        console.error(error);
      } finally {
        textArea.remove();
      }
    };
  }

  const handleOpen = () => {
    setShowConfirmModal(true);
  }

  const handleClose = () => setOpen(false);
  const onCloseConfirmModal = () => setShowConfirmModal(false);

  const customizeBookingForm = async () => {
    const data = {
      bookFormId: emailNotification.bookFormId,
      welcomeTitle: welcomeTitle,
      welcomeMessage: welcomeMessage,
      thankyouTitle: thankyouTitle,
      thankyouMessage: thankyouMessage,
      whatsnextMessage,
    }
    await dispatch(customBookingForm(data));
    setOpen(true);
  }

  const onConfirmYes = () => {
    onCloseConfirmModal();
    customizeBookingForm();
  }

  return (
    <Container maxWidth="lg">
      <Grid
        className="page-title"
        container
        justifyContent='space-between'
        sx={{ my: { sm: 2, md: 5 }, marginInline: 'auto' }}
      >
        <Grid item xs={12} sm={6}>
          <Typography sx={{ color: '#fff', fontSize: '1.5rem', fontWeight: 600 }}>Booking Form Settings</Typography>
          <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 500, mt: '3px', opacity: 0.75 }}>Personalize your booking form notifications and other settings.</Typography>
        </Grid>
        <Grid item container direction='row' justifyContent='flex-end' alignItems="center" xs={12} sm={6} sx={{ my: 1 }}>
          <Button color="primary" variant="contained" sx={{ fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', color: '#fff', padding: '10px 30px' }} onClick={handleOpen}>Save Settings</Button>
        </Grid>
      </Grid>
      <Grid
        container
        display='flex'
        direction='row'
        alignItems="flex-start"
        spacing={3}
      >
        <Grid item container lg={8}>
          <Container
            direction='column'
            sx={{ backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e2d', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', padding: '40px !important' }}
          >
            <Grid>
              <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', color: theme.palette.mode ==="light" ? "#3f4254" : "#fff" }}>Welcome Message</Typography>
              <Typography sx={{ fontSize: '11px', mt: 0.5, fontWeight: 400, color: theme.palette.mode ==="light" ? "#a1a5b7" : "#565674" }}>This is a personalized message that greets your clients when they first launch your form.</Typography>
            </Grid>
            <Divider sx={{ mt: 2 }} />
            <Grid container alignItems="center" sx={{ mt: 2 }}>
              <Grid item xs={12} md="auto">
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, width: 200, color: theme.palette.mode ==="light" ? "#3f4254" : "#cdcdde" }}>Title</Typography>
              </Grid>
              <Grid item xs>
                <TextField
                  variant="standard"
                  fullWidth
                  sx={{ mt: { xs: 1 } }}
                  value={welcomeTitle}
                  onChange={e => setWelcomeTitle(e.target.value)}
                  InputProps={{
                    disableUnderline: true,
                    style: {
                      padding: '8px 20px',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                    }
                  }}
                />
              </Grid>
            </Grid>
            <Grid container sx={{ mt: 2, py: 1 }}>
              <Grid item xs={12} md="auto">
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, width: 200, color: theme.palette.mode ==="light" ? "#3f4254" : "#cdcdde" }}>Message</Typography>
              </Grid>
              <Grid item xs>
                <TextField
                  variant="standard"
                  fullWidth
                  sx={{ mt: { xs: 1 } }}
                  multiline
                  rows={5}
                  value={welcomeMessage}
                  onChange={e => setWelcomeMessage(e.target.value)}
                  InputProps={{
                    disableUnderline: true,
                    style: {
                      padding: '15px 20px',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Container>
          <Container
            direction='column'
            sx={{
              backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e2d',
              borderBottomLeftRadius: '8px',
              borderBottomRightRadius: '8px',
              px: '40px !important',
              paddingBottom: '40px !important'
            }}>
            <Grid>
              <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', color: theme.palette.mode ==="light" ? "#3f4254" : "#fff" }}>Thank you Message</Typography>
              <Typography sx={{ fontSize: '11px', mt: 0.5, fontWeight: 400, color: theme.palette.mode ==="light" ? "#a1a5b7" : "#565674" }}>This is a personalized message for clients who have completed and submitted the form.</Typography>
            </Grid>
            <Divider sx={{ mt: 2 }} />
            <Grid container alignItems="center" sx={{ mt: 2 }}>
              <Grid item xs={12} md="auto">
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, width: 200, color: theme.palette.mode ==="light" ? "#3f4254" : "#cdcdde" }}>Title</Typography>
              </Grid>
              <Grid item xs>
                <TextField
                  variant="standard"
                  fullWidth
                  sx={{ mt: { xs: 1 } }}
                  value={thankyouTitle}
                  onChange={e => setThankyouTitle(e.target.value)}
                  InputProps={{
                    disableUnderline: true,
                    style: {
                      padding: '8px 20px',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                    }
                  }}
                />
              </Grid>
            </Grid>
            <Grid container sx={{ mt: 2, py: 1 }}>
              <Grid item xs={12} md="auto">
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, width: 200, color: theme.palette.mode ==="light" ? "#3f4254" : "#cdcdde" }}>Message</Typography>
              </Grid>
              <Grid item xs>
                <TextField
                  variant="standard"
                  fullWidth
                  sx={{ mt: { xs: 1 } }}
                  multiline
                  rows={4}
                  value={thankyouMessage}
                  onChange={e => setThankyouMessage(e.target.value)}
                  InputProps={{
                    disableUnderline: true,
                    style: {
                      padding: '15px 20px',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                    }
                  }}
                />
              </Grid>
            </Grid>
            <Grid container sx={{ mt: 2, py: 1 }}>
              <Grid item xs={12} md="auto">
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, width: 200, color: theme.palette.mode ==="light" ? "#3f4254" : "#cdcdde" }}>What's Next?</Typography>
              </Grid>
              <Grid item xs>
                <TextField
                  variant="standard"
                  fullWidth
                  sx={{ mt: { xs: 1 } }}
                  multiline
                  rows={7}
                  value={whatsnextMessage}
                  onChange={e => setWhatsnextMessage(e.target.value)}
                  InputProps={{
                    disableUnderline: true,
                    style: {
                      padding: '15px 20px',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Container>
        </Grid>
        <Grid
          item
          container
          lg={4}
          justifyContent='center'
          alignItems='center'
        >
          <Container
            direction='column'
            sx={{ backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e2d', borderRadius: 2, padding: '40px !important' }}
          >
            <Grid sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', lineHeight: '1.2', color: theme.palette.mode === 'light' ? '#3f4254' : '#cdcdde' }}>Displaying your Booking Form</Typography>
              <Divider sx={{ mt: 2, mb: 2 }} />
              <Typography sx={{ fontSize: '0.8rem', mt: 0.5, fontWeight: 400 }}>
                <p>1. Personalize your form messaging on this page. Advanced screening & preferences can be found in your user settings.</p>
                <p>2. Obtain your embed code, and place it on your website, profiles, or social media. </p>
				<Box component="img" src={IMG_BOOKING_OVERVIEW} sx={{ width: '100%', objectFit: 'contain', mt: 0 }} alt="Booking Form Overview" />
				<p>Not sure? Submit a support ticket, we can help integrate your booking form for free.</p>
              </Typography>
              <Grid sx={{ mt: 4.5, mb: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', lineHeight: '1.2' }}>Direct Link</Typography>
                <Divider sx={{ mt: 2, mb: 1.5 }} />
                <Typography sx={{ fontSize: '0.8rem', mt: 0.5, color: theme.palette.mode ==="light" ? "#181c32" : "#fff" }}>This is the direct link to your booking form.</Typography>
              </Grid>
              <TextField
                variant="standard"
                fullWidth
                value={valueDirect}
                InputProps={{
                  disableUnderline: true,
                  style: {
                    padding: '8px 20px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 400,
                  },
                }}
                sx={{ mt: 1 }}
              />
              <Tooltip
                open={openLinkCopyTooltip}
                onClose={handleTooltipClose}
                title={'Link copied!'}
                leaveDelay={500}
                placement='top'
                arrow
                enterTouchDelay={0}
              >
                <Button
                  onClick={handleCopyLink}
                  sx={{
                    mt: 1,
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 600,
                    minWidth: '7rem',
                    backgroundColor: theme.palette.mode === 'light' ? '#f5f8fa' : '#2B2B40',
                    color: theme.palette.mode === 'dark' && '#6D6D80',
                    padding: '11px 20px',
                    '&:hover': {
                      color: theme.palette.mode === 'dark' && '#009EF7',
                    }
                  }}
                >
                  Copy Code
                </Button>
              </Tooltip>
            </Grid>
          </Container>
        </Grid>
      </Grid>
      <ScrollToTop
        smooth
        color="#009EF7"
      />
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 360,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            padding: '48px 32px 42px',
          }}
        >
          <Grid sx={{ width: '80%', margin: 'auto' }} className="text-center">
            <Grid container justifyContent='center' alignItems='center' className="swal-question swal-close" sx={{ borderColor: "#50CD89" }}>
              <Grid className="swal-x-content" sx={{ fontSize: '3em', color: '#50CD89' }}>&#10004;</Grid>
            </Grid>
            <Typography sx={{ fontSize: '2rem', textAlign: 'center', fontWeight: 700, mt: 2, mb: 1.5 }}>
              Success!
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', textAlign: 'center', fontWeight: 400 }}>
              Your settings have been saved.
            </Typography>
            <Grid item container direction='row' justifyContent='center' alignItems='center' display="flex" sx={{ mt: 3 }}>
              <Button
                color="success"
                variant="contained"
                sx={{ fontSize: '0.9rem', fontWeight: 500, textTransform: 'none', color: '#fff', padding: '12px 30px' }}
                onClick={handleClose}
              >
                Continue
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <ConfirmModal
        visible={showConfirmModal}
        symbol="?"
        title="Save changes?"
		description="Your booking form will be updated with these settings."
        titleYes="Confirm"
        titleNo="Cancel"
        onClickYes={onConfirmYes}
        onClickNo={onCloseConfirmModal}
      />
    </Container>
  );
}

export default CustomizeFormPage;
